import { useState } from "react";
import type { Order, Payment } from "@probetalab/contracts";
import { getHtml, run } from "../lib/api.js";
import { money } from "../lib/format.js";
import { printHtml } from "../lib/print.js";
import { useBranch } from "../lib/session.js";
import { useToast } from "../lib/toast.js";
import { errorMessage } from "../lib/useQuery.js";
import { PaymentEditor, type PaymentDraft, type PendingPayment } from "./PaymentEditor.js";
import { useTicketWidth } from "./PrintActions.js";
import { Button, Money, Sheet } from "./ui.js";

/** The slice of an order the payment sheet needs; both `Order` and `OrderDetail` satisfy it. */
export type PayableOrder = Pick<Order, "id" | "folio" | "total_cents" | "paid_cents" | "balance_cents">;

/**
 * Register payments against an order's balance through `payment.record` (same permissions, audit, cash
 * drawer of the acting user and cash-close lock as everywhere else). Used by the order page
 * («Registrar pago») and by Entrega («Liquidar», Fer 2026-11-03 09:40: the patient comes for the
 * results and pays what is owed). With `settle` the sheet shows Total / Pagado / Adeudo, and after
 * saving it stays open to offer the payment ticket instead of closing.
 */
export function PaySheet({
  open,
  onClose,
  order,
  onPaid,
  settle = false,
}: {
  open: boolean;
  onClose: () => void;
  order: PayableOrder;
  onPaid: (o: Order) => void;
  settle?: boolean;
}) {
  const branch = useBranch();
  const toast = useToast();
  const [width] = useTicketWidth();
  const [drafts, setDrafts] = useState<PaymentDraft[]>([]);
  const [pending, setPending] = useState<PendingPayment | null>(null);
  const [busy, setBusy] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [done, setDone] = useState<{ balance: number; change: number } | null>(null);
  // In this sheet the intent is to pay: the line in the inputs counts even if untouched (its
  // amount defaults to the balance shown as the placeholder).
  const toSave = pending ? [...drafts, pending.draft] : drafts;

  function close() {
    setDone(null);
    setDrafts([]);
    onClose();
  }

  async function save() {
    setBusy(true);
    try {
      let latest: Order | null = null;
      for (const p of toSave) {
        const r = await run<"payment.record", { payment: Payment; order: Order }>("payment.record", {
          order_id: order.id,
          branch_id: branch.id,
          method: p.method,
          amount_cents: p.amount_cents,
          reference: p.reference,
          tendered_cents: p.tendered_cents,
        });
        latest = r.order;
      }
      if (!latest) return;
      onPaid(latest);
      const message = latest.balance_cents > 0 ? `Abono registrado. Adeudo ${money(latest.balance_cents)}` : "Solicitud liquidada.";
      if (settle) {
        const change = toSave.reduce((n, p) => n + (p.tendered_cents !== null && p.tendered_cents > p.amount_cents ? p.tendered_cents - p.amount_cents : 0), 0);
        setDone({ balance: latest.balance_cents, change });
        setDrafts([]);
      } else {
        toast.success(message);
        close();
      }
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  async function printTicket() {
    setPrinting(true);
    try {
      void printHtml(await getHtml("orderTicketHtml", { params: { id: order.id }, query: { width } }));
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setPrinting(false);
    }
  }

  return (
    <Sheet
      open={open}
      onClose={close}
      title={done ? "Pago registrado" : settle ? `Liquidar · ${order.folio}` : `Registrar pago · adeudo ${money(order.balance_cents)}`}
      footer={
        done ? (
          <>
            <Button variant="secondary" icon="print" busy={printing} onClick={() => void printTicket()}>
              Imprimir ticket
            </Button>
            <Button variant="primary" icon="check" onClick={close}>
              Listo
            </Button>
          </>
        ) : (
          <>
            <Button variant="ghost" onClick={close}>
              Cancelar
            </Button>
            <Button variant="primary" icon="check" busy={busy} disabled={!toSave.length} onClick={() => void save()}>
              {settle ? "Pagar" : `Guardar ${toSave.length > 1 ? "pagos" : "pago"}`}
            </Button>
          </>
        )
      }
    >
      {done ? (
        <div className="stack" data-testid="settle-done">
          <p style={{ color: "var(--brand-ground)", fontWeight: 600 }}>{done.balance > 0 ? `Abono registrado. Adeudo ${money(done.balance)}` : "Solicitud liquidada."}</p>
          {done.change > 0 && <p className="pay-change">Cambio: {money(done.change)}</p>}
        </div>
      ) : (
        <div className="stack">
          {settle && (
            <div className="totals" data-testid="settle-summary">
              <div className="trow">
                <span>Total</span>
                <Money cents={order.total_cents} />
              </div>
              <div className="trow">
                <span>Pagado</span>
                <Money cents={order.paid_cents} />
              </div>
              <div className="trow due">
                <span>Adeudo</span>
                <Money cents={order.balance_cents} />
              </div>
            </div>
          )}
          <PaymentEditor key={open ? "open" : "closed"} due={order.balance_cents} payments={drafts} onChange={setDrafts} onPending={setPending} onEnter={() => toSave.length > 0 && !busy && void save()} addLabel={settle ? "Agregar otra forma de pago" : undefined} />
        </div>
      )}
    </Sheet>
  );
}
